import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { graphql } from "../../../../__generated__/gql";
import EditableCell from "../components/EditableCell/EditableCell";
import OptionsCell from "../components/OptionsCell/OptionsCell";
import { useMutation, useQuery } from "urql";
import { useCallback, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import EditableTable from "../components/EditableTable/EditableTable";
import UnsavedChangesModal from "../../../../components/UnsavedChangesModal/UnsavedChangesModal";
import Loader from "../../../../ui/Loader/Loader";
import { useToast } from "../../../../ui/Toast/ToastContext";

const AudiencesQuery = graphql(/* GraphQL */ `
  query audiences {
    audiences {
      id
      name
      description
      benefits
      terminology
    }
  }
`);

const CreateAudiencesMutation = graphql(/* GraphQL */ `
  mutation createAudiences($input: [CreateAudienceInput!]!) {
    createAudiences(input: $input) {
      id
      name
      description
      benefits
      terminology
    }
  }
`);

const UpdateAudiencesMutation = graphql(/* GraphQL */ `
  mutation updateAudiences($input: [UpdateAudienceInput!]!) {
    updateAudiences(input: $input) {
      id
      name
      description
      benefits
      terminology
    }
  }
`);

const DeleteAudiencesMutation = graphql(/* GraphQL */ `
  mutation deleteAudiences($ids: [String!]!) {
    deleteAudiences(ids: $ids) {
      id
    }
  }
`);

type AudiencesTableRow = {
  id?: string;
  name?: string | null;
  description?: string | null;
  benefits?: string | null;
  terminology?: string | null;
};

type AudiencesTableFormData = {
  rows: AudiencesTableRow[];
};

const columnHelper = createColumnHelper<AudiencesTableRow>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: ColumnDef<AudiencesTableRow, any>[] = [
  columnHelper.accessor("name", {
    header: "Audience Segment",
    size: 300,
    cell: EditableCell,
    meta: {
      tooltip: "The name of your audience segment",
    },
  }),
  columnHelper.accessor("description", {
    header: "Description",
    minSize: 300,
    cell: EditableCell,
    meta: {
      tooltip: "A detailed overview of the the audience segment",
    },
  }),
  columnHelper.accessor("benefits", {
    header: "Product benefits",
    minSize: 300,
    cell: EditableCell,
    meta: {
      tooltip:
        "The aspects of your product or service that the audience segment benefits from",
    },
  }),
  columnHelper.accessor("terminology", {
    header: "Key terminology",
    minSize: 300,
    cell: EditableCell,
    meta: {
      tooltip: "Any specific terminology used by the audience segment",
    },
  }),
  columnHelper.display({
    id: "Options",
    size: 72,
    meta: {
      sticky: true,
    },
    cell: OptionsCell,
  }),
];

function AudiencesTable({ data }: { data: AudiencesTableRow[] }) {
  const [, updateAudiences] = useMutation(UpdateAudiencesMutation);
  const [, createAudiences] = useMutation(CreateAudiencesMutation);
  const [, deleteAudiences] = useMutation(DeleteAudiencesMutation);
  const { showToast } = useToast();

  const methods = useForm<AudiencesTableFormData>({
    defaultValues: { rows: data },
  });

  const onSubmit = useCallback(
    async (data: AudiencesTableFormData) => {
      const rowsWithIndex = data.rows.map((row, index) => ({ index, ...row }));
      const audiencesToCreate = rowsWithIndex.filter((f) => !f.id);
      const audiencesToUpdate = rowsWithIndex.filter(
        (f, index): f is typeof f & { id: string } =>
          !!(f.id && methods.getFieldState(`rows.${index}`).isDirty),
      );
      const audiencesToDelete =
        methods.formState.defaultValues?.rows?.filter(
          (f): f is typeof f & { id: string } =>
            !!(f?.id && !data.rows.find((row) => row.id === f.id)),
        ) ?? [];
      const [createdAudiences, updatedAudiences] = await Promise.all([
        audiencesToCreate.length
          ? createAudiences({
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              input: audiencesToCreate.map(({ index, id, ...f }) => f),
            })
          : null,
        audiencesToUpdate.length
          ? updateAudiences({
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              input: audiencesToUpdate.map(({ index, ...f }) => f),
            })
          : null,
        audiencesToDelete.length
          ? deleteAudiences({
              ids: audiencesToDelete.map((f) => f?.id),
            })
          : null,
      ]);

      const newAudiences = data.rows.map((audience, index) => {
        const createdAudienceIndex = audiencesToCreate.findIndex(
          (f) => f.index === index,
        );

        if (createdAudienceIndex >= 0) {
          return createdAudiences?.data?.createAudiences[createdAudienceIndex];
        }

        const updatedAudienceIndex = audiencesToUpdate.findIndex(
          (f) => f.index === index,
        );

        if (updatedAudienceIndex >= 0) {
          return updatedAudiences?.data?.updateAudiences[updatedAudienceIndex];
        }

        return audience;
      });

      methods.reset({ rows: newAudiences.filter(Boolean) });
      showToast({
        title: "Audiences saved",
        variant: "success",
      });
    },
    [createAudiences, deleteAudiences, methods, showToast, updateAudiences],
  );

  return (
    <FormProvider {...methods}>
      <UnsavedChangesModal isDirty={methods.formState.isDirty} />
      <EditableTable
        propertyName="rows"
        onSubmit={onSubmit}
        columns={columns}
        title="Audiences"
        description="Manage the different audiences you write content for. Macaw automatically selects the most relevant audience(s) for an article."
        resourceName={{ plural: "audiences", singular: "audience" }}
        defaultValue={{
          id: "",
          name: "",
          description: "",
          benefits: "",
          terminology: "",
        }}
        maxRows={45}
      />
    </FormProvider>
  );
}

function Audiences() {
  const [audiencesResponse] = useQuery({
    query: AudiencesQuery,
    requestPolicy: "network-only",
  });

  const data: AudiencesTableRow[] = useMemo(() => {
    if (!audiencesResponse.data?.audiences?.length) {
      return [];
    }

    return audiencesResponse.data.audiences.map((audience) => ({
      id: audience.id,
      name: audience.name,
      description: audience.description,
      benefits: audience.benefits,
      terminology: audience.terminology,
    }));
  }, [audiencesResponse]);

  if (audiencesResponse.fetching) {
    return (
      <div
        style={{
          padding: "32px 0",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Loader />
      </div>
    );
  }

  return <AudiencesTable data={data} />;
}

export default Audiences;
